import React, { useState, useCallback, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  FiX,
  FiUpload,
  FiShield,
  FiCheckCircle,
  FiInfo,
  FiTrash2,
  FiUploadCloud,
  FiKey,
  FiList,
  FiSend,
} from 'react-icons/fi';
import { GoSync, GoCheck, GoAlert, GoShieldX } from 'react-icons/go';
import { IoCubeOutline } from 'react-icons/io5';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import api from '../../services/api';

import { tokens, surface, hairline } from '../../styles/designTokens';

/* Paleta de cores única por texto (cidade/estado) — mesmo padrão do Revisor SPED */
const TAG_PALETTE = [
  '#dc2626', '#ea580c', '#65a30d', '#16a34a', '#0d9488',
  '#0891b2', '#2563eb', '#4f46e5', '#9333ea', '#db2777',
];
const getColorFromText = (text) => {
  const seed = (text || '').toString().trim().toLowerCase();
  if (!seed) return TAG_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
};
const cleanCompanyName = (name) => {
  if (!name) return '';
  return name.replace(/:\d+$/, '').trim();
};
const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const c = cnpj.replace(/\D/g, '');
  return c.length === 14
    ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : cnpj;
};

/* ============================================================
 * MAIN COMPONENT
 * ============================================================ */
export default function CertificateModal({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState(0); // 0=list, 1=upload, 2=password, 3=uploading
  const [certificates, setCertificates] = useState([]);
  const [existingCertificates, setExistingCertificates] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [globalPassword, setGlobalPassword] = useState('');
  const [customizedCerts, setCustomizedCerts] = useState(new Set());
  const [projectCnpj, setProjectCnpj] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certToDelete, setCertToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Trigger to force refresh of certificate list
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /* ----------- fetch existing ----------- */
  const fetchExistingCertificates = useCallback(async () => {
    setLoadingExisting(true);
    try {
      const selectedProjectId = localStorage.getItem('vidal_selected_project');
      if (!selectedProjectId) {
        setLoadingExisting(false);
        return;
      }
      const response = await api.get(`/certificates/list/${selectedProjectId}`);
      if (response.data.success) {
        setExistingCertificates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching existing certificates:', error);
    } finally {
      setLoadingExisting(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchExistingCertificates();
  }, [isOpen, refreshTrigger, fetchExistingCertificates]);

  /* ----------- fetch project cnpj ----------- */
  useEffect(() => {
    const fetchProjectCnpj = async () => {
      try {
        const selectedProjectId = localStorage.getItem('vidal_selected_project');
        if (!selectedProjectId) return;
        const response = await api.get(`/projects/${selectedProjectId}`);
        if (response.data?.cnpj) setProjectCnpj(response.data.cnpj);
      } catch (error) {
        console.error('Error fetching project CNPJ:', error);
      }
    };
    if (isOpen) fetchProjectCnpj();
  }, [isOpen]);

  /* ----------- upload progress listener ----------- */
  useEffect(() => {
    const handleProgress = (event) => setUploadProgress(event.detail);
    window.addEventListener('uploadProgress', handleProgress);
    return () => window.removeEventListener('uploadProgress', handleProgress);
  }, []);

  useEffect(() => {
    if (!isOpen) setUploadProgress(0);
  }, [isOpen]);

  /* ----------- dropzone onDrop ----------- */
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const certFiles = acceptedFiles.filter(
        (file) =>
          file.name.toLowerCase().endsWith('.pfx') ||
          file.name.toLowerCase().endsWith('.p12')
      );

      if (certFiles.length === 0) {
        toast.warning('Selecione apenas arquivos .pfx ou .p12');
        return;
      }

      try {
        const formData = new FormData();
        certFiles.forEach((file) => formData.append('certificates', file));

        const response = await api.post('/certificates/preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
          const newCertificates = response.data.certificates.map(
            (certData, index) => {
              const certCnpjDigits = certData.cnpj
                ? certData.cnpj.replace(/\D/g, '')
                : '';
              const existingMatch = existingCertificates.find((ec) => {
                const ecCnpjDigits = ec.cnpj ? ec.cnpj.replace(/\D/g, '') : '';
                return ecCnpjDigits && ecCnpjDigits === certCnpjDigits;
              });
              const isUpdate = !!existingMatch;
              const isExpired = existingMatch
                ? new Date(existingMatch.validTo) < new Date()
                : false;

              return {
                file: certFiles[index],
                fileName: certData.fileName,
                companyName: certData.companyName || '',
                cnpj: certData.cnpj || '',
                password: '',
                verified: false,
                verifying: false,
                verifyError: '',
                isUpdate,
                isExpired,
                existingCert: existingMatch,
              };
            }
          );

          setCertificates((prev) => [...prev, ...newCertificates]);
          setFiles((prev) => [...prev, ...certFiles]);
          setStep(2);
        } else {
          toast.error('Erro ao processar certificados: ' + response.data.message);
        }
      } catch (error) {
        console.error('Error parsing certificates:', error);
        toast.error('Erro ao processar certificados. Verifique se os arquivos são válidos.');
      }
    },
    [existingCertificates]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/x-pkcs12': ['.pfx', '.p12'] },
    multiple: true,
    disabled: step === 2,
  });

  /* ----------- verify cert ----------- */
  const handleVerifyCertificate = async (index) => {
    const cert = certificates[index];
    if (!cert.password) return;

    setCertificates((prev) =>
      prev.map((c, i) =>
        i === index ? { ...c, verifying: true, verifyError: '' } : c
      )
    );

    try {
      const formData = new FormData();
      formData.append('certificate', cert.file);
      formData.append('password', cert.password);

      const response = await api.post('/certificates/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        const certData = response.data.certificate;

        let cnpjMatch = true;
        let cnpjWarning = '';

        if (
          projectCnpj &&
          certData.cnpj &&
          certData.cnpj !== 'CNPJ não encontrado'
        ) {
          const certCnpjDigits = certData.cnpj.replace(/\D/g, '');
          const projectCnpjDigits = projectCnpj.replace(/\D/g, '');
          const certCnpjRoot = certCnpjDigits.slice(0, 8);
          const projectCnpjRoot = projectCnpjDigits.slice(0, 8);

          if (certCnpjRoot !== projectCnpjRoot) {
            cnpjMatch = false;
            cnpjWarning = `CNPJ raiz do certificado (${certCnpjRoot}) não corresponde ao projeto (${projectCnpjRoot})`;
          }
        }

        setCertificates((prev) =>
          prev.map((c, i) =>
            i === index
              ? {
                  ...c,
                  verified: true,
                  verifying: false,
                  companyName: certData.companyName,
                  cnpj: certData.cnpj,
                  cnpjMatch,
                  verifyError: cnpjMatch ? '' : cnpjWarning,
                }
              : c
          )
        );
      } else {
        setCertificates((prev) =>
          prev.map((c, i) =>
            i === index
              ? {
                  ...c,
                  verifying: false,
                  verified: false,
                  verifyError:
                    response.data.message || 'Erro ao verificar certificado',
                }
              : c
          )
        );
      }
    } catch (error) {
      console.error('Error verifying certificate:', error);
      let errorMessage = 'Erro ao verificar certificado';
      if (error.response?.status === 401) errorMessage = 'Senha incorreta';
      else if (error.response?.data?.message)
        errorMessage = error.response.data.message;

      setCertificates((prev) =>
        prev.map((c, i) =>
          i === index
            ? {
                ...c,
                verifying: false,
                verified: false,
                verifyError: errorMessage,
              }
            : c
        )
      );
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setCertificates((prev) => prev.filter((_, i) => i !== index));
    if (files.length === 1) setStep(1);
  };

  const handlePasswordChange = (index, password) => {
    setCertificates((prev) =>
      prev.map((cert, i) => (i === index ? { ...cert, password } : cert))
    );
    setCustomizedCerts((prev) => new Set([...prev, index]));
  };

  const handleGlobalPasswordChange = (password) => {
    setGlobalPassword(password);
    setCertificates((prev) =>
      prev.map((cert, index) =>
        customizedCerts.has(index) ? cert : { ...cert, password }
      )
    );
  };

  const handleCheckAllCertificates = async () => {
    if (!globalPassword) return;
    for (let i = 0; i < certificates.length; i++) {
      if (!certificates[i].verified) await handleVerifyCertificate(i);
    }
  };

  /* ----------- final upload ----------- */
  const handleSubmit = async () => {
    if (uploading) return;

    const emptyPasswords = certificates.filter((c) => !c.password);
    if (emptyPasswords.length > 0) {
      toast.warning('Preencha a senha de todos os certificados');
      return;
    }

    setUploading(true);
    setStep(3);

    try {
      const formData = new FormData();
      const selectedProjectId = localStorage.getItem('vidal_selected_project');
      formData.append('projectId', selectedProjectId);

      certificates.forEach((cert, index) => {
        formData.append('certificates', cert.file);
        formData.append(`password_${index}`, cert.password);
      });

      const response = await api.post('/certificates/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          window.dispatchEvent(
            new CustomEvent('uploadProgress', { detail: percentCompleted })
          );
        },
      });

      if (response.data.success) {
        const results = response.data.results || [];
        const succeeded = results.filter((r) => r.success);
        const updated = results.filter(
          (r) => r.success && r.action === 'updated'
        );
        const created = results.filter(
          (r) => r.success && (r.action === 'created' || !r.action)
        );
        const failed = results.filter((r) => !r.success);

        if (failed.length > 0) {
          if (succeeded.length > 0) {
            toast.warning(
              `${succeeded.length} processado(s), ${failed.length} falharam`,
              { autoClose: 5000 }
            );
          } else {
            toast.error(
              `Erro ao enviar: ${failed.map((f) => f.fileName).join(', ')}`,
              { autoClose: 5000 }
            );
          }
        } else if (updated.length > 0 && created.length > 0) {
          toast.success(
            `${updated.length} atualizado(s) e ${created.length} novo(s) enviado(s)`,
            { autoClose: 4000 }
          );
        } else if (updated.length > 0) {
          toast.success(
            `${updated.length} certificado(s) atualizado(s) com sucesso`,
            { autoClose: 3000 }
          );
        } else {
          toast.success(
            `${succeeded.length} certificado(s) enviado(s) com sucesso`,
            { autoClose: 3000 }
          );
        }

        setUploading(false);
        setFiles([]);
        setCertificates([]);
        setGlobalPassword('');
        setCustomizedCerts(new Set());

        await fetchExistingCertificates();
        setRefreshTrigger((prev) => prev + 1);
        // Notify other screens (Certificates list page)
        window.dispatchEvent(new CustomEvent('certificates-updated'));

        await new Promise((resolve) => setTimeout(resolve, 100));
        setStep(0);
      } else {
        throw new Error(response.data.message || 'Erro ao enviar certificados');
      }
    } catch (error) {
      console.error('Error uploading certificates:', error);
      toast.error(
        'Erro ao enviar certificados: ' +
          (error.response?.data?.message || error.message),
        { autoClose: 5000 }
      );
      setUploading(false);
      setStep(2);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setFiles([]);
    setCertificates([]);
    setStep(0);
    setGlobalPassword('');
    setCustomizedCerts(new Set());
    onClose();
  };

  const openDeleteModal = (cert) => {
    setCertToDelete(cert);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCertToDelete(null);
    setDeleting(false);
  };

  const confirmDeleteCertificate = async () => {
    if (!certToDelete) return;
    setDeleting(true);
    try {
      const selectedProjectId = localStorage.getItem('vidal_selected_project');
      const response = await api.delete(
        `/certificates/delete/${selectedProjectId}/${certToDelete.cnpj}`
      );
      if (response.data.success) {
        toast.success('Certificado excluído com sucesso', { autoClose: 3000 });
        await fetchExistingCertificates();
        setRefreshTrigger((prev) => prev + 1);
        window.dispatchEvent(new CustomEvent('certificates-updated'));
        await new Promise((resolve) => setTimeout(resolve, 100));
        closeDeleteModal();
      } else {
        throw new Error(response.data.message || 'Erro ao excluir certificado');
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error(
        'Erro ao excluir certificado: ' +
          (error.response?.data?.message || error.message),
        { autoClose: 5000 }
      );
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  /* ============================================================
   * RENDER
   * ============================================================ */
  const stepConfig = [
    { id: 0, label: 'Lista', icon: FiList },
    { id: 1, label: 'Upload', icon: FiUploadCloud },
    { id: 2, label: 'Senhas', icon: FiKey },
    { id: 3, label: 'Envio', icon: FiSend },
  ];

  const titleByStep = {
    0: 'Certificados Digitais',
    1: 'Enviar Certificados',
    2: 'Configurar Senhas',
    3: 'Enviando Certificados',
  };

  return (
    <>
      <ModalOverlay onClick={handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          {/* HEADER */}
          <ModalHeader>
            <div className="header-top">
              <div className="title-block">
                <span className="eyebrow">CERTIFICADOS DIGITAIS</span>
                <h3>{titleByStep[step]}</h3>
              </div>
              <button
                className="close-btn"
                onClick={handleClose}
                disabled={uploading}
                title="Fechar"
              >
                <FiX />
              </button>
            </div>

            {/* STEPPER */}
            <div className="stepper">
              {stepConfig.map((s, i) => {
                const isActive = step === s.id;
                const isDone = step > s.id;
                const Icon = s.icon;
                return (
                  <React.Fragment key={s.id}>
                    <div
                      className={`step-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                    >
                      <div className="step-dot">
                        {isDone ? <GoCheck /> : <Icon />}
                      </div>
                      <span className="step-label">{s.label}</span>
                    </div>
                    {i < stepConfig.length - 1 && (
                      <div className={`step-line ${isDone ? 'done' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </ModalHeader>

          {/* BODY */}
          <ModalBody>
            {step === 0 && (
              <Step0List>
                <ScrollArea>
                  {loadingExisting ? (
                    <div className="loading-state">
                      <GoSync className="spin" />
                      <p>Carregando certificados...</p>
                    </div>
                  ) : existingCertificates.length > 0 ? (
                    <>
                      <div className="list-header">
                        <span className="eyebrow-mono">
                          CADASTRADOS · {existingCertificates.length}
                        </span>
                      </div>
                      <div className="cert-list">
                        {existingCertificates.map((cert) => {
                          const isExpired =
                            new Date(cert.validTo) < new Date();
                          const statusClass = isExpired ? 'danger' : 'ok';
                          const cityColor = getColorFromText(cert.city);
                          const stateColor = getColorFromText(cert.state);
                          return (
                            <div
                              key={cert.id || cert.cnpj}
                              className={`cert-row ${statusClass}`}
                            >
                              <div
                                className="row-icon"
                                style={{ color: cert.color || getColorFromText(cert.companyName) }}
                              >
                                <IoCubeOutline />
                              </div>
                              <div className="row-info">
                                <h5>{cleanCompanyName(cert.companyName)}</h5>
                                <div className="row-meta">
                                  <span className="meta-mono">
                                    {formatCNPJ(cert.cnpj)}
                                  </span>
                                  {cert.city && (
                                    <span
                                      className="loc-tag"
                                      style={{
                                        background: `${cityColor}14`,
                                        color: cityColor,
                                        borderColor: `${cityColor}38`,
                                      }}
                                    >
                                      {cert.city}
                                    </span>
                                  )}
                                  {cert.state && (
                                    <span
                                      className="loc-tag uf"
                                      style={{
                                        background: `${stateColor}14`,
                                        color: stateColor,
                                        borderColor: `${stateColor}38`,
                                      }}
                                    >
                                      {cert.state}
                                    </span>
                                  )}
                                  <span
                                    className={`meta-status ${statusClass}`}
                                    title={`${isExpired ? 'Expirado' : 'Válido'} até ${new Date(cert.validTo).toLocaleDateString('pt-BR')}`}
                                  >
                                    {isExpired ? <GoShieldX /> : <GoCheck />}
                                    {isExpired ? 'Expirado' : 'Válido'}
                                  </span>
                                </div>
                              </div>
                              <button
                                className="row-delete"
                                onClick={() => openDeleteModal(cert)}
                                title="Excluir"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <FiShield />
                      </div>
                      <h4>Nenhum certificado cadastrado</h4>
                      <p>Clique em "Enviar Certificados" para adicionar</p>
                    </div>
                  )}
                </ScrollArea>
                <StepFooter>
                  <PrimaryButton onClick={() => setStep(1)}>
                    <FiUpload />
                    Enviar Certificados
                  </PrimaryButton>
                </StepFooter>
              </Step0List>
            )}

            {step === 1 && (
              <Step1Upload>
                <ScrollArea>
                  <InfoBox>
                    <FiInfo />
                    <p>
                      Arraste certificados digitais (.pfx ou .p12) ou clique
                      para selecionar. Múltiplos arquivos suportados.
                    </p>
                  </InfoBox>

                  <Dropzone
                    {...getRootProps()}
                    className={`${isDragActive ? 'active' : ''} ${files.length > 0 ? 'has-files' : ''}`}
                  >
                    <input {...getInputProps()} />
                    <div className="drop-icon">
                      {files.length > 0 ? (
                        <FiCheckCircle />
                      ) : (
                        <FiUploadCloud />
                      )}
                    </div>
                    <div className="drop-text">
                      {files.length > 0 ? (
                        <>
                          <span className="drop-eyebrow">SELECIONADOS</span>
                          <h4>
                            {files.length} arquivo
                            {files.length > 1 ? 's' : ''}
                          </h4>
                          <p>Clique para adicionar mais ou arraste novos</p>
                        </>
                      ) : (
                        <>
                          <span className="drop-eyebrow">
                            ARRASTE OU CLIQUE
                          </span>
                          <h4>
                            {isDragActive
                              ? 'Solte os arquivos aqui'
                              : 'Adicionar certificados'}
                          </h4>
                          <p>Aceita .pfx · .p12 · múltiplos arquivos</p>
                        </>
                      )}
                    </div>
                  </Dropzone>
                </ScrollArea>
                <StepFooter>
                  <PrimaryButton
                    disabled={files.length === 0}
                    onClick={() => setStep(2)}
                  >
                    <FiKey />
                    Configurar Senhas
                  </PrimaryButton>
                </StepFooter>
              </Step1Upload>
            )}

            {step === 2 && (
              <Step2Passwords>
                <ScrollArea>
                  <InfoBox>
                    <FiInfo />
                    <p>
                      Informe a senha de cada certificado para validá-lo e
                      armazená-lo com segurança.
                    </p>
                  </InfoBox>

                  {/* Global password section */}
                  <GlobalPasswordCard>
                  <div className="gp-header">
                    <span className="eyebrow-mono">SENHA GERAL · OPCIONAL</span>
                  </div>
                  <div className="gp-input-row">
                    <input
                      type="password"
                      placeholder="Aplicar a mesma senha a todos"
                      value={globalPassword}
                      onChange={(e) =>
                        handleGlobalPasswordChange(e.target.value)
                      }
                      disabled={uploading}
                      autoComplete="new-password"
                    />
                    <button
                      className="gp-check-btn"
                      onClick={handleCheckAllCertificates}
                      disabled={uploading || !globalPassword}
                      title="Verificar todos"
                    >
                      <GoCheck />
                      Checar todos
                    </button>
                  </div>
                    <span className="gp-hint">
                      Use para aplicar a mesma senha em massa.
                    </span>
                  </GlobalPasswordCard>

                  {/* Certificate list */}
                  <CertPasswordList>
                  {certificates.map((cert, index) => {
                    const isMismatch =
                      cert.verified && cert.cnpjMatch === false;
                    const accentClass = cert.verified
                      ? isMismatch
                        ? 'warn'
                        : 'ok'
                      : '';
                    return (
                      <div
                        key={index}
                        className={`cert-pass-row ${accentClass}`}
                      >
                        <div className="cpr-header">
                          <div className="cpr-icon">
                            <FiShield />
                          </div>
                          <div className="cpr-info">
                            {cert.verified && cert.companyName ? (
                              <>
                                <div className="cpr-title-row">
                                  <h5>{cert.companyName}</h5>
                                  {cert.isUpdate && (
                                    <span
                                      className={`cpr-tag ${cert.isExpired ? 'danger' : 'brand'}`}
                                    >
                                      {cert.isExpired
                                        ? 'RENOVAÇÃO'
                                        : 'ATUALIZAÇÃO'}
                                    </span>
                                  )}
                                </div>
                                <span className="cpr-cnpj">
                                  {cert.cnpj}
                                </span>
                              </>
                            ) : (
                              <span className="cpr-filename">
                                {cert.fileName}
                              </span>
                            )}
                          </div>
                          <button
                            className="cpr-remove"
                            onClick={() => removeFile(index)}
                            title="Remover"
                          >
                            <FiX />
                          </button>
                        </div>

                        <div className="cpr-password-row">
                          <input
                            type="password"
                            placeholder="Senha do certificado"
                            value={cert.password}
                            onChange={(e) =>
                              handlePasswordChange(index, e.target.value)
                            }
                            disabled={uploading || cert.verified}
                            autoComplete="new-password"
                          />
                          <button
                            className={`cpr-verify-btn ${cert.verified ? 'verified' : ''}`}
                            onClick={() => handleVerifyCertificate(index)}
                            disabled={
                              uploading || !cert.password || cert.verified
                            }
                          >
                            {cert.verifying ? (
                              <GoSync className="spin" />
                            ) : cert.verified ? (
                              <>
                                <GoCheck />
                                Verificado
                              </>
                            ) : (
                              <>
                                <GoCheck />
                                Verificar
                              </>
                            )}
                          </button>
                        </div>

                        {cert.verified && !cert.verifyError && (
                          <div
                            className={`cpr-feedback ${cert.cnpjMatch ? 'success' : 'warning'}`}
                          >
                            {cert.cnpjMatch ? <GoCheck /> : <GoAlert />}
                            <span>
                              {cert.cnpjMatch
                                ? 'Certificado verificado com sucesso'
                                : 'Verificado, mas com aviso'}
                            </span>
                          </div>
                        )}

                        {cert.verifyError && (
                          <div className="cpr-feedback error">
                            <GoAlert />
                            <span>{cert.verifyError}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </CertPasswordList>
                </ScrollArea>
                <StepFooter>
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={
                      uploading ||
                      certificates.some(
                        (c) => !c.verified || c.cnpjMatch === false
                      )
                    }
                  >
                    <FiUpload />
                    Enviar Certificados
                  </PrimaryButton>
                </StepFooter>
              </Step2Passwords>
            )}

            {step === 3 && (
              <Step3Uploading>
                <ScrollArea>
                  <div className="uploading-content">
                    <div className="upload-spinner">
                      <GoSync className="spin" />
                    </div>

                    <div className="upload-message">
                      <h3>Enviando certificados</h3>
                      <p>
                        Aguarde enquanto processamos {certificates.length}{' '}
                        certificado{certificates.length > 1 ? 's' : ''}
                      </p>
                    </div>

                    <ProgressBlock>
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <span className="progress-pct">{uploadProgress}%</span>
                    </ProgressBlock>

                    <UploadingList>
                      {certificates.map((cert, index) => (
                        <div key={index} className="ul-row">
                          <div className="ul-icon">
                            <FiShield />
                          </div>
                          <div className="ul-info">
                            <span className="ul-name">
                              {cert.companyName || cert.fileName}
                            </span>
                            <span className="ul-cnpj">{cert.cnpj}</span>
                          </div>
                          <GoCheck className="ul-check" />
                        </div>
                      ))}
                    </UploadingList>
                  </div>
                </ScrollArea>
              </Step3Uploading>
            )}
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>

      {/* DELETE CONFIRMATION SUB-MODAL */}
      {deleteModalOpen && certToDelete && (
        <DeleteOverlay onClick={closeDeleteModal}>
          <DeleteContainer onClick={(e) => e.stopPropagation()}>
            <div className="del-icon">
              <FiTrash2 />
            </div>
            <h3>Excluir Certificado</h3>
            <p>
              Tem certeza que deseja excluir este certificado? Esta ação não
              pode ser desfeita.
            </p>
            <div className="del-cert-card">
              <span className="del-cert-name">{certToDelete.companyName}</span>
              <span className="del-cert-cnpj">{certToDelete.cnpj}</span>
            </div>
            <div className="del-actions">
              <button
                className="del-cancel"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="del-confirm"
                onClick={confirmDeleteCertificate}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <GoSync className="spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <FiTrash2 />
                    Excluir
                  </>
                )}
              </button>
            </div>
          </DeleteContainer>
        </DeleteOverlay>
      )}
    </>
  );
}

/* ============================================================
 * KEYFRAMES & MIXINS
 * ============================================================ */
const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

/* ============================================================
 * MAIN MODAL OVERLAY / CONTAINER
 * ============================================================ */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 540px;
  height: min(720px, 92vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * MODAL HEADER + STEPPER
 * ============================================================ */
const ModalHeader = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  flex-shrink: 0;

  .header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 16px;

    .title-block {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
      flex: 1;

      .eyebrow {
        ${eyebrow};
        font-size: 0.58em;
        opacity: 0.4;
      }

      h3 {
        margin: 2px 0 0 0;
        font-size: 1.1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.015em;
      }
    }

    .close-btn {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex-shrink: 0;

      svg { font-size: 1em; }

      &:hover:not(:disabled) {
        opacity: 1;
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &:disabled {
        opacity: 0.25;
        cursor: not-allowed;
      }
    }
  }

  /* ============== STEPPER ============== */
  .stepper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px 2px;

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      .step-dot {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: 50%;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.85em; }
      }

      .step-label {
        ${mono};
        font-size: 0.58em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: opacity ${tokens.durBase} ${tokens.easeOut};
      }

      &.active {
        .step-dot {
          background: ${tokens.brandSoft};
          border-color: ${tokens.brand};
          color: ${tokens.brand};
          opacity: 1;
          box-shadow: 0 0 0 3px ${tokens.brandRing};
        }
        .step-label {
          color: ${tokens.brand};
          opacity: 1;
        }
      }

      &.done {
        .step-dot {
          background: ${tokens.brand};
          border-color: ${tokens.brand};
          color: white;
          opacity: 1;
        }
        .step-label {
          opacity: 0.7;
        }
      }
    }

    .step-line {
      flex: 1;
      height: 1px;
      background: ${({ theme }) => hairline(theme)};
      transition: background ${tokens.durBase} ${tokens.easeOut};
      margin-top: -14px;

      &.done {
        background: ${tokens.brand};
        opacity: 0.5;
      }
    }
  }
`;

/* ============================================================
 * MODAL BODY (no scroll — children handle scroll)
 * ============================================================ */
const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* ============================================================
 * SCROLL AREA — used inside each step
 * ============================================================ */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 14px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

/* ============================================================
 * STEP FOOTER — fixed bottom action bar
 * ============================================================ */
const StepFooter = styled.div`
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ============================================================
 * SHARED INFO BOX
 * ============================================================ */
const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  margin-bottom: 16px;

  svg {
    color: ${tokens.brand};
    opacity: 0.85;
    font-size: 0.95em;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 0.78em;
    line-height: 1.5;
    color: ${({ theme }) => theme.text};
    opacity: 0.75;
    letter-spacing: -0.005em;
  }
`;

/* ============================================================
 * PRIMARY BUTTON (auto width, fits content + small padding)
 * ============================================================ */
const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 28px;
  background: ${tokens.brand};
  color: white;
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.85em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.25);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ============================================================
 * STEP WRAPPERS — flex column, no overflow (children handle it)
 * ============================================================ */
const StepWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* ============================================================
 * STEP 0 — LIST
 * ============================================================ */
const Step0List = styled(StepWrapper)`
  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px 20px;
    text-align: center;

    .empty-icon {
      width: 56px;
      height: 56px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};

      svg { font-size: 1.4em; }
    }

    > svg {
      font-size: 2em;
      color: ${tokens.brand};
    }

    h4 {
      margin: 0;
      font-size: 0.95em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }
  }

  .list-header {
    margin-bottom: 12px;

    .eyebrow-mono {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.45;
    }
  }

  .cert-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  /* Linha de lista no padrão do modal de Novo Relatório (Revisor SPED) */
  .cert-row {
    display: grid;
    grid-template-columns: 26px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 10px 14px;
    background: ${({ theme }) => surface(theme, 1)};
    border: none;
    border-radius: ${tokens.radiusSm};
    cursor: default;
    transition: background ${tokens.durBase} ${tokens.easeOut};

    &:hover {
      background: ${({ theme }) => surface(theme, 0)};
      .row-delete { opacity: 0.65; }
    }

    /* Ícone tipo "cubo" colorido por status (sem fundo) */
    .row-icon {
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;

      svg { font-size: 1.1em; }
    }

    &.ok .row-icon     { color: ${tokens.brand}; }
    &.danger .row-icon { color: ${tokens.danger}; }

    .row-info {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 5px;

      h5 {
        margin: 0;
        font-size: 0.82em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Tags estilo Notion: CNPJ neutro + status colorido */
      .row-meta {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;

        .meta-mono {
          ${mono};
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          border-radius: ${tokens.radiusPill};
          background: ${({ theme }) => surface(theme, 2)};
          border: 1px solid ${({ theme }) => hairline(theme)};
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          font-size: 0.62em;
          font-weight: 500;
          letter-spacing: 0.02em;
        }

        /* Tags de cidade/estado coloridas (cor via inline style) */
        .loc-tag {
          display: inline-flex;
          align-items: center;
          padding: 3px 9px;
          border-radius: ${tokens.radiusPill};
          font-family: ${tokens.fontSans};
          font-size: 0.62em;
          font-weight: 600;
          letter-spacing: 0.01em;
          border: 1px solid;
          text-transform: capitalize;
          white-space: nowrap;

          &.uf {
            ${mono};
            text-transform: uppercase;
            font-size: 0.58em;
            letter-spacing: 0.04em;
          }
        }

        .meta-sep { display: none; }

        .meta-status {
          ${mono};
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: ${tokens.radiusPill};
          font-size: 0.6em;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          border: 1px solid;

          svg { font-size: 1em; }

          &.ok {
            background: ${tokens.brandSoft};
            color: ${tokens.brand};
            border-color: ${tokens.brandRing};
          }
          &.danger {
            background: ${tokens.dangerSoft};
            color: ${tokens.danger};
            border-color: rgba(239, 68, 68, 0.22);
          }
        }
      }
    }

    .row-delete {
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      opacity: 0;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.9em; }

      &:hover {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.3);
        color: ${tokens.danger};
        opacity: 1 !important;
      }
    }
  }
`;

/* ============================================================
 * STEP 1 — UPLOAD
 * ============================================================ */
const Step1Upload = styled(StepWrapper)`
  /* ScrollArea inside this step becomes a flex column so the
     Dropzone can stretch to fill the remaining space below InfoBox */
  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 14px;

    /* neutralize default margin so flex gap controls spacing */
    > *:first-child { margin-bottom: 0; }
  }
`;

const Dropzone = styled.div`
  flex: 1;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 24px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px dashed ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  cursor: pointer;
  text-align: center;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover {
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};
  }

  &.active {
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};
    box-shadow: 0 0 0 4px ${tokens.brandRing};
  }

  &.has-files {
    border-style: solid;
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};

    .drop-icon {
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
    }
  }

  .drop-icon {
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 50%;
    color: ${tokens.brand};
    transition: all ${tokens.durBase} ${tokens.easeOut};

    svg { font-size: 1.5em; }
  }

  .drop-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .drop-eyebrow {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.5;
    }

    h4 {
      margin: 4px 0 2px 0;
      font-size: 0.95em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;
    }
  }
`;

/* ============================================================
 * STEP 2 — PASSWORDS
 * ============================================================ */
const Step2Passwords = styled(StepWrapper)`
  .eyebrow-mono {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.5;
  }
`;

const GlobalPasswordCard = styled.div`
  padding: 12px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .gp-input-row {
    display: flex;
    gap: 8px;

    input {
      flex: 1;
      padding: 9px 12px;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      outline: none;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &::placeholder { opacity: 0.4; }
      &:focus { border-color: ${tokens.brandRing}; }
      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }

    .gp-check-btn {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 9px 14px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusSm};
      font-family: ${tokens.fontSans};
      font-size: 0.74em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  .gp-hint {
    ${mono};
    font-size: 0.62em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    letter-spacing: 0.01em;
  }
`;

const CertPasswordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .cert-pass-row {
    padding: 12px 14px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 2px;
      border-radius: 0 2px 2px 0;
      opacity: 0;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
    }

    &.ok::before {
      background: ${tokens.brand};
      opacity: 0.7;
    }
    &.warn::before {
      background: ${tokens.warn};
      opacity: 0.7;
    }
  }

  .cpr-header {
    display: grid;
    grid-template-columns: 36px 1fr 28px;
    gap: 10px;
    align-items: center;

    .cpr-icon {
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: ${tokens.radiusMd};
      color: ${tokens.brand};

      svg { font-size: 1em; }
    }

    .cpr-info {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .cpr-title-row {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
      }

      h5 {
        margin: 0;
        font-size: 0.85em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .cpr-tag {
        ${mono};
        padding: 2px 6px;
        font-size: 0.55em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border-radius: ${tokens.radiusPill};
        border: 1px solid;

        &.brand {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }

        &.danger {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
        }
      }

      .cpr-cnpj {
        ${mono};
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: 0.01em;
      }

      .cpr-filename {
        ${mono};
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        letter-spacing: 0.01em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .cpr-remove {
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.9em; }

      &:hover {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.25);
        color: ${tokens.danger};
        opacity: 1;
      }
    }
  }

  .cpr-password-row {
    display: flex;
    gap: 8px;

    input {
      flex: 1;
      padding: 9px 12px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      outline: none;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &::placeholder { opacity: 0.4; }
      &:focus { border-color: ${tokens.brandRing}; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    .cpr-verify-btn {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 9px 14px;
      background: transparent;
      color: ${tokens.brand};
      border: 1px solid ${tokens.brandRing};
      border-radius: ${tokens.radiusSm};
      font-family: ${tokens.fontSans};
      font-size: 0.74em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandSoft};
      }

      &.verified {
        background: ${tokens.brand};
        color: white;
        border-color: ${tokens.brand};
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.55;

        &.verified { opacity: 1; }
      }
    }
  }

  .cpr-feedback {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-radius: ${tokens.radiusSm};
    font-size: 0.74em;
    letter-spacing: -0.005em;

    svg { font-size: 0.95em; flex-shrink: 0; }

    &.success {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border: 1px solid ${tokens.brandRing};
    }

    &.warning {
      background: ${tokens.warnSoft};
      color: ${tokens.warn};
      border: 1px solid rgba(245, 158, 11, 0.22);
    }

    &.error {
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border: 1px solid rgba(239, 68, 68, 0.22);
    }
  }
`;

/* ============================================================
 * STEP 3 — UPLOADING
 * ============================================================ */
const Step3Uploading = styled(StepWrapper)`
  .uploading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 16px 0 8px;
    width: 100%;
  }

  .upload-spinner {
    width: 64px;
    height: 64px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.brandSoft};
    border: 1px solid ${tokens.brandRing};
    border-radius: 50%;
    color: ${tokens.brand};

    svg { font-size: 1.8em; }
  }

  .upload-message {
    text-align: center;

    h3 {
      ${mono};
      margin: 0 0 4px 0;
      font-size: 0.85em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: ${({ theme }) => theme.text};
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.72em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;
    }
  }
`;

const ProgressBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  .progress-track {
    width: 100%;
    height: 4px;
    background: ${({ theme }) => surface(theme, 2)};
    border-radius: ${tokens.radiusPill};
    overflow: hidden;

    .progress-fill {
      height: 100%;
      background: ${tokens.brand};
      border-radius: ${tokens.radiusPill};
      transition: width 0.3s ${tokens.easeOut};
    }
  }

  .progress-pct {
    ${mono};
    font-size: 0.85em;
    font-weight: 500;
    color: ${tokens.brand};
    letter-spacing: -0.01em;
  }
`;

const UploadingList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 240px;
  overflow-y: auto;
  padding-top: 4px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }

  .ul-row {
    display: grid;
    grid-template-columns: 32px 1fr auto;
    gap: 10px;
    align-items: center;
    padding: 9px 12px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};

    .ul-icon {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border-radius: ${tokens.radiusSm};
      color: ${tokens.brand};

      svg { font-size: 0.95em; }
    }

    .ul-info {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;

      .ul-name {
        font-size: 0.78em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        letter-spacing: -0.005em;
      }

      .ul-cnpj {
        ${mono};
        font-size: 0.62em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        letter-spacing: 0.01em;
      }
    }

    .ul-check {
      font-size: 1em;
      color: ${tokens.brand};
    }
  }
`;

/* ============================================================
 * DELETE CONFIRMATION SUB-MODAL
 * ============================================================ */
const DeleteOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const DeleteContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 420px;
  padding: 28px 24px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

  .del-icon {
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.dangerSoft};
    border: 1px solid rgba(239, 68, 68, 0.22);
    border-radius: 50%;
    color: ${tokens.danger};
    margin-bottom: 14px;

    svg { font-size: 1.4em; }
  }

  h3 {
    margin: 0 0 6px 0;
    font-size: 1.05em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.01em;
  }

  > p {
    margin: 0 0 16px 0;
    font-size: 0.82em;
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    line-height: 1.5;
    letter-spacing: -0.005em;
  }

  .del-cert-card {
    width: 100%;
    padding: 12px 14px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    margin-bottom: 16px;

    .del-cert-name {
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
    }

    .del-cert-cnpj {
      ${mono};
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.02em;
    }
  }

  .del-actions {
    display: flex;
    gap: 8px;
    width: 100%;

    button {
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 16px;
      border-radius: ${tokens.radiusMd};
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;

      svg { font-size: 0.95em; }

      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }

    .del-cancel {
      background: transparent;
      color: ${({ theme }) => theme.text};
      border: 1px solid ${({ theme }) => hairline(theme)};
      opacity: 0.85;

      &:hover:not(:disabled) {
        background: ${({ theme }) => surface(theme, 1)};
        opacity: 1;
      }
    }

    .del-confirm {
      background: ${tokens.danger};
      color: white;
      border: 1px solid ${tokens.danger};

      &:hover:not(:disabled) {
        filter: brightness(1.05);
        transform: translateY(-1px);
      }
    }
  }
`;
